import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  FiPlus,
  FiSearch,
  FiDownload,
  FiMail,
  FiCheck,
  FiX,
  FiShield,
  FiPackage,
} from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'
import currencies from '../../utils/currencies'

export default function Items() {
  const [items, setItems] = useState([])
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [currencyFilter, setCurrencyFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [downloadingItemId, setDownloadingItemId] = useState(null)
  const [sendingInvoice, setSendingInvoice] = useState(false)

  const [invoiceData, setInvoiceData] = useState({
    customerEmail: '',
    customerAddress: '',
    notes: ''
  })

  useEffect(() => {
    fetchItems()
    fetchUserProfile()
  }, [currentPage, statusFilter, searchTerm, currencyFilter])

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get('/auth/profile')
      setUser(response.data.user)
    } catch (error) {
      console.error('Error fetching user profile:', error)
    }
  }

  const fetchItems = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/items', {
        params: {
          page: currentPage,
          limit: 12,
          status: statusFilter,
          search: searchTerm,
          currency: currencyFilter
        }
      })

      setItems(response.data.items)
      setTotalPages(response.data.totalPages)
    } catch (error) {
      toast.error('Error loading items')
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadInvoice = async (itemId) => {
    setDownloadingItemId(itemId)
    try {
      const response = await axios.get(`/items/${itemId}/invoice`, {
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `invoice-${itemId}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('Invoice downloaded successfully')
    } catch (error) {
      toast.error('Error downloading invoice')
    } finally {
      setDownloadingItemId(null)
    }
  }

  const handleSendInvoice = async (e) => {
    e.preventDefault()
    setSendingInvoice(true)

    try {
      await axios.post(`/items/${selectedItem._id}/send-invoice`, invoiceData)
      toast.success('Invoice sent successfully')
      setShowInvoiceModal(false)
      setInvoiceData({
        customerEmail: '',
        customerAddress: '',
        notes: ''
      })
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error sending invoice')
    } finally {
      setSendingInvoice(false)
    }
  }

  const handlePaymentApproval = async (itemId, approve) => {
    try {
      await axios.post(`/items/${itemId}/approve-payment`, { approve })
      toast.success(`Payment ${approve ? 'approved' : 'rejected'} successfully`)
      fetchItems()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error processing payment approval')
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      paid: 'bg-success-100 text-success-800',
      unpaid: 'bg-error-100 text-error-800',
      pending: 'bg-warning-100 text-warning-800'
    }
    return badges[status] || 'bg-gray-100 text-gray-800'
  }

  const statusOptions = [
    { value: 'all', label: 'All Items' },
    { value: 'paid', label: 'Paid' },
    { value: 'unpaid', label: 'Unpaid' },
    { value: 'pending', label: 'Pending' }
  ]

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">My Items</h1>
          <p className="text-gray-600">Manage your marketplace items and invoices</p>
        </div>
        <Link to="/add-item" className="btn-primary mt-4 sm:mt-0">
          <FiPlus className="w-4 h-4 mr-2" />
          Add New Item
        </Link>
      </div>

      {/* Admin Notice */}
      {!user?.adminConfig?.isAdmin && (
        <div className="card bg-blue-50 border-blue-200 mb-6">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <FiShield className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-blue-900 mb-1">Edit & Delete Permissions</h3>
              <p className="text-sm text-blue-800">
                Only User Admins have permission to edit or delete items. Regular users are restricted.
              </p>
              <Link
                to="/admin-access"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium mt-2 inline-block"
              >
                Get Admin Access →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:w-56">
            <select
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
              className="input-field"
            >
              <option value="all">All Currencies</option>
              {currencies.map(c => (
                <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Items */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : items.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {items.map((item) => (
              <div key={item._id} className="card hover:shadow-lg transition-shadow p-4">
                {item.image && (
                  <img src={item.image} alt={item.name} className="w-full h-48 object-cover rounded-lg mb-4" />
                )}
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
                    {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-2">Customer: {item.customerName || 'Not set'}</p>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>

                {/* Updated Price / Quantity / Totals */}
                <div className="mb-4 text-sm text-gray-600 space-y-1">
                  <p>Price: {item.currency || 'NGN'} {item.price?.toLocaleString()}</p>
                  <p>Quantity: {item.quantity}</p>
                  <p>Subtotal: {item.currency || 'NGN'} {item.subTotal?.toLocaleString()}</p>
                  {item.taxAmount > 0 ? (
                    <p>Tax ({item.taxRate || 0}%): {item.currency || 'NGN'} {item.taxAmount?.toLocaleString()}</p>
                  ) : (
                    <p className="font-medium text-green-600">Tax Included</p>
                  )}
                  <p className="font-semibold">Total: {item.currency || 'NGN'} {item.totalAmount?.toLocaleString()}</p>
                </div>

                <div className="text-xs text-gray-500 mb-4">
                  <p>Invoice: {item.invoiceNumber}</p>
                  <p>Created: {new Date(item.createdAt).toLocaleDateString()}</p>
                </div>

                {item.paymentRequest && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-yellow-800 mb-2">Payment confirmation requested</p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePaymentApproval(item._id, true)}
                        className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center"
                      >
                        <FiCheck className="mr-1 w-3 h-3" /> Approve
                      </button>
                      <button
                        onClick={() => handlePaymentApproval(item._id, false)}
                        className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 flex items-center"
                      >
                        <FiX className="mr-1 w-3 h-3" /> Reject
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center border-t pt-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleDownloadInvoice(item._id)}
                      disabled={downloadingItemId === item._id}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50 transition"
                      title="Download Invoice"
                    >
                      {downloadingItemId === item._id ? (
                        <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                      ) : (
                        <FiDownload className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedItem(item)
                        setShowInvoiceModal(true)
                      }}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                      title="Send Invoice"
                    >
                      <FiMail className="w-4 h-4" />
                    </button>
                  </div>
                  {!user?.adminConfig?.isAdmin && (
                    <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">Admin Only</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mt-8">
              <button
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 sm:px-4 py-2 border border-gray-300 rounded text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Previous
              </button>
              {totalPages <= 5 ? (
                [...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-3 sm:px-4 py-2 border rounded text-sm sm:text-base transition-colors ${
                      currentPage === i + 1 ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))
              ) : (
                <>
                  {currentPage > 2 && (
                    <>
                      <button onClick={() => setCurrentPage(1)} className="px-3 sm:px-4 py-2 border border-gray-300 rounded text-sm sm:text-base hover:bg-gray-50">1</button>
                      {currentPage > 3 && <span className="px-2 py-2">...</span>}
                    </>
                  )}
                  {[Math.max(1, currentPage - 1), currentPage, Math.min(totalPages, currentPage + 1)].filter(p => p > 0 && p <= totalPages && p !== currentPage - 1 && p !== currentPage + 1).map(p => (
                    <button key={p} onClick={() => setCurrentPage(p)} className={`px-3 sm:px-4 py-2 border rounded text-sm sm:text-base ${currentPage === p ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 hover:bg-gray-50'}`}>{p}</button>
                  ))}
                  {currentPage < totalPages - 1 && (
                    <>
                      {currentPage < totalPages - 2 && <span className="px-2 py-2">...</span>}
                      <button onClick={() => setCurrentPage(totalPages)} className="px-3 sm:px-4 py-2 border border-gray-300 rounded text-sm sm:text-base hover:bg-gray-50">{totalPages}</button>
                    </>
                  )}
                </>
              )}
              <button
                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 sm:px-4 py-2 border border-gray-300 rounded text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <FiPackage className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No items found</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Get started by adding your first item'}
          </p>
          <Link to="/add-item" className="btn-primary">
            <FiPlus className="w-4 h-4 mr-2" />
            Add Your First Item
          </Link>
        </div>
      )}

      {/* Send Invoice Modal */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-sm sm:max-w-md w-full max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Send Invoice - {selectedItem?.name}
              </h3>
            </div>
            <form onSubmit={handleSendInvoice} className="p-4 sm:p-6 space-y-4">
              <input
                type="email"
                required
                value={invoiceData.customerEmail}
                onChange={(e) => setInvoiceData(prev => ({ ...prev, customerEmail: e.target.value }))}
                className="input-field"
                placeholder="Customer Email"
              />
              <textarea
                value={invoiceData.customerAddress}
                onChange={(e) => setInvoiceData(prev => ({ ...prev, customerAddress: e.target.value }))}
                className="input-field"
                rows="3"
                placeholder="Customer Address"
              />
              <textarea
                value={invoiceData.notes}
                onChange={(e) => setInvoiceData(prev => ({ ...prev, notes: e.target.value }))}
                className="input-field"
                rows="2"
                placeholder="Notes"
              />
              <div className="flex gap-3 pt-4 flex-col sm:flex-row border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowInvoiceModal(false)}
                  className="flex-1 btn-secondary"
                  disabled={sendingInvoice}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingInvoice}
                  className="flex-1 btn-primary flex items-center justify-center gap-2"
                >
                  {sendingInvoice ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    'Send Invoice'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}






// import { useState, useEffect } from 'react'
// import { Link } from 'react-router-dom'
// import {
//   FiPlus,
//   FiSearch,
//   FiDownload,
//   FiMail,
//   FiCheck,
//   FiX,
//   FiShield,
//   FiPackage,
// } from 'react-icons/fi'
// import axios from 'axios'
// import toast from 'react-hot-toast'
// import currencies from '../../utils/currencies'

// export default function Items() {
//   const [items, setItems] = useState([])
//   const [user, setUser] = useState(null)
//   const [loading, setLoading] = useState(true)
//   const [searchTerm, setSearchTerm] = useState('')
//   const [statusFilter, setStatusFilter] = useState('all')
//   const [currencyFilter, setCurrencyFilter] = useState('all')
//   const [currentPage, setCurrentPage] = useState(1)
//   const [totalPages, setTotalPages] = useState(1)
//   const [showInvoiceModal, setShowInvoiceModal] = useState(false)
//   const [selectedItem, setSelectedItem] = useState(null)
//   const [downloadingItemId, setDownloadingItemId] = useState(null)
//   const [sendingInvoice, setSendingInvoice] = useState(false)

//   const [invoiceData, setInvoiceData] = useState({
//     customerEmail: '',
//     customerAddress: '',
//     notes: ''
//   })

//   useEffect(() => {
//     fetchItems()
//     fetchUserProfile()
//   }, [currentPage, statusFilter, searchTerm, currencyFilter])

//   const fetchUserProfile = async () => {
//     try {
//       const response = await axios.get('/auth/profile')
//       setUser(response.data.user)
//     } catch (error) {
//       console.error('Error fetching user profile:', error)
//     }
//   }

//   const fetchItems = async () => {
//     try {
//       setLoading(true)
//       const response = await axios.get('/items', {
//         params: {
//           page: currentPage,
//           limit: 12,
//           status: statusFilter,
//           search: searchTerm,
//           currency: currencyFilter
//         }
//       })

//       setItems(response.data.items)
//       setTotalPages(response.data.totalPages)
//     } catch (error) {
//       toast.error('Error loading items')
//     } finally {
//       setLoading(false)
//     }
//   }

//   const handleDownloadInvoice = async (itemId) => {
//     setDownloadingItemId(itemId)
//     try {
//       const response = await axios.get(`/items/${itemId}/invoice`, {
//         responseType: 'blob'
//       })

//       const url = window.URL.createObjectURL(new Blob([response.data]))
//       const link = document.createElement('a')
//       link.href = url
//       link.setAttribute('download', `invoice-${itemId}.pdf`)
//       document.body.appendChild(link)
//       link.click()
//       link.remove()
//       window.URL.revokeObjectURL(url)

//       toast.success('Invoice downloaded successfully')
//     } catch (error) {
//       toast.error('Error downloading invoice')
//     } finally {
//       setDownloadingItemId(null)
//     }
//   }

//   const handleSendInvoice = async (e) => {
//     e.preventDefault()
//     setSendingInvoice(true)

//     try {
//       await axios.post(`/items/${selectedItem._id}/send-invoice`, invoiceData)
//       toast.success('Invoice sent successfully')
//       setShowInvoiceModal(false)
//       setInvoiceData({
//         customerEmail: '',
//         customerAddress: '',
//         notes: ''
//       })
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error sending invoice')
//     } finally {
//       setSendingInvoice(false)
//     }
//   }

//   const handlePaymentApproval = async (itemId, approve) => {
//     try {
//       await axios.post(`/items/${itemId}/approve-payment`, { approve })
//       toast.success(`Payment ${approve ? 'approved' : 'rejected'} successfully`)
//       fetchItems()
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error processing payment approval')
//     }
//   }

//   const getStatusBadge = (status) => {
//     const badges = {
//       paid: 'bg-success-100 text-success-800',
//       unpaid: 'bg-error-100 text-error-800',
//       pending: 'bg-warning-100 text-warning-800'
//     }
//     return badges[status] || 'bg-gray-100 text-gray-800'
//   }

//   const statusOptions = [
//     { value: 'all', label: 'All Items' },
//     { value: 'paid', label: 'Paid' },
//     { value: 'unpaid', label: 'Unpaid' },
//     { value: 'pending', label: 'Pending' }
//   ]

//   return (
//     <div className="max-w-7xl mx-auto">
//       {/* Header */}
//       <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
//         <div>
//           <h1 className="text-2xl font-bold text-gray-900 mb-2">My Items</h1>
//           <p className="text-gray-600">Manage your marketplace items and invoices</p>
//         </div>
//         <Link to="/add-item" className="btn-primary mt-4 sm:mt-0">
//           <FiPlus className="w-4 h-4 mr-2" />
//           Add New Item
//         </Link>
//       </div>

//       {/* Admin Notice */}
//       {!user?.adminConfig?.isAdmin && (
//         <div className="card bg-blue-50 border-blue-200 mb-6">
//           <div className="flex items-start space-x-3">
//             <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
//               <FiShield className="w-4 h-4 text-blue-600" />
//             </div>
//             <div>
//               <h3 className="text-sm font-semibold text-blue-900 mb-1">Edit & Delete Permissions</h3>
//               <p className="text-sm text-blue-800">
//                 Only User Admins have permission to edit or delete items. Regular users are restricted.
//               </p>
//               <Link
//                 to="/admin-access"
//                 className="text-sm text-blue-600 hover:text-blue-700 font-medium mt-2 inline-block"
//               >
//                 Get Admin Access →
//               </Link>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Filters */}
//       <div className="card mb-6">
//         <div className="flex flex-col sm:flex-row gap-4">
//           <div className="flex-1 relative">
//             <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
//             <input
//               type="text"
//               placeholder="Search items..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               className="input-field pl-10"
//             />
//           </div>
//           <div className="sm:w-48">
//             <select
//               value={statusFilter}
//               onChange={(e) => setStatusFilter(e.target.value)}
//               className="input-field"
//             >
//               {statusOptions.map(option => (
//                 <option key={option.value} value={option.value}>
//                   {option.label}
//                 </option>
//               ))}
//             </select>
//           </div>
//           <div className="sm:w-56">
//             <select
//               value={currencyFilter}
//               onChange={(e) => setCurrencyFilter(e.target.value)}
//               className="input-field"
//             >
//               <option value="all">All Currencies</option>
//               {currencies.map(c => (
//                 <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
//               ))}
//             </select>
//           </div>
//         </div>
//       </div>

//       {/* Items */}
//       {loading ? (
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//           {[...Array(6)].map((_, i) => (
//             <div key={i} className="card animate-pulse">
//               <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
//               <div className="h-4 bg-gray-200 rounded mb-2"></div>
//               <div className="h-4 bg-gray-200 rounded w-2/3"></div>
//             </div>
//           ))}
//         </div>
//       ) : items.length > 0 ? (
//         <>
//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
//             {items.map((item) => (
//               <div key={item._id} className="card hover:shadow-lg transition-shadow p-4">
//                 {item.image && (
//                   <img src={item.image} alt={item.name} className="w-full h-48 object-cover rounded-lg mb-4" />
//                 )}
//                 <div className="flex justify-between items-start mb-2">
//                   <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
//                   <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
//                     {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
//                   </span>
//                 </div>
//                 <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>

//                 {/* Updated Price / Quantity / Totals */}
//                 <div className="mb-4 text-sm text-gray-600 space-y-1">
//                   <p>Price: {item.currency || 'NGN'} {item.price?.toLocaleString()}</p>
//                   <p>Quantity: {item.quantity}</p>
//                   <p>Subtotal: {item.currency || 'NGN'} {item.subTotal?.toLocaleString()}</p>
//                   <p>Tax ({item.taxRate || 0}%): {item.currency || 'NGN'} {item.taxAmount?.toLocaleString()}</p>
//                   <p className="font-semibold">Total: {item.currency || 'NGN'} {item.totalAmount?.toLocaleString()}</p>
//                 </div>

//                 <div className="text-xs text-gray-500 mb-4">
//                   <p>Invoice: {item.invoiceNumber}</p>
//                   <p>Created: {new Date(item.createdAt).toLocaleDateString()}</p>
//                 </div>

//                 {item.paymentRequest && (
//                   <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
//                     <p className="text-sm text-yellow-800 mb-2">Payment confirmation requested</p>
//                     <div className="flex space-x-2">
//                       <button
//                         onClick={() => handlePaymentApproval(item._id, true)}
//                         className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center"
//                       >
//                         <FiCheck className="mr-1 w-3 h-3" /> Approve
//                       </button>
//                       <button
//                         onClick={() => handlePaymentApproval(item._id, false)}
//                         className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 flex items-center"
//                       >
//                         <FiX className="mr-1 w-3 h-3" /> Reject
//                       </button>
//                     </div>
//                   </div>
//                 )}

//                 <div className="flex justify-between items-center border-t pt-4">
//                   <div className="flex space-x-2">
//                     <button
//                       onClick={() => handleDownloadInvoice(item._id)}
//                       disabled={downloadingItemId === item._id}
//                       className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50 transition"
//                       title="Download Invoice"
//                     >
//                       {downloadingItemId === item._id ? (
//                         <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
//                       ) : (
//                         <FiDownload className="w-4 h-4" />
//                       )}
//                     </button>
//                     <button
//                       onClick={() => {
//                         setSelectedItem(item)
//                         setShowInvoiceModal(true)
//                       }}
//                       className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
//                       title="Send Invoice"
//                     >
//                       <FiMail className="w-4 h-4" />
//                     </button>
//                   </div>
//                   {!user?.adminConfig?.isAdmin && (
//                     <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">Admin Only</div>
//                   )}
//                 </div>
//               </div>
//             ))}
//           </div>

//           {/* Pagination */}
//           {totalPages > 1 && (
//             <div className="flex justify-center gap-2">
//               <button
//                 onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
//                 disabled={currentPage === 1}
//                 className="px-4 py-2 border rounded disabled:opacity-50"
//               >
//                 Previous
//               </button>
//               {[...Array(totalPages)].map((_, i) => (
//                 <button
//                   key={i}
//                   onClick={() => setCurrentPage(i + 1)}
//                   className={`px-4 py-2 border rounded ${
//                     currentPage === i + 1 ? 'bg-primary-600 text-white' : 'hover:bg-gray-50'
//                   }`}
//                 >
//                   {i + 1}
//                 </button>
//               ))}
//               <button
//                 onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
//                 disabled={currentPage === totalPages}
//                 className="px-4 py-2 border rounded disabled:opacity-50"
//               >
//                 Next
//               </button>
//             </div>
//           )}
//         </>
//       ) : (
//         <div className="text-center py-12">
//           <FiPackage className="w-16 h-16 text-gray-400 mx-auto mb-4" />
//           <h3 className="text-lg font-medium text-gray-900 mb-2">No items found</h3>
//           <p className="text-gray-600 mb-6">
//             {searchTerm || statusFilter !== 'all'
//               ? 'Try adjusting your search or filter criteria'
//               : 'Get started by adding your first item'}
//           </p>
//           <Link to="/add-item" className="btn-primary">
//             <FiPlus className="w-4 h-4 mr-2" />
//             Add Your First Item
//           </Link>
//         </div>
//       )}

//       {/* Send Invoice Modal */}
//       {showInvoiceModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-xl max-w-md w-full p-6">
//             <h3 className="text-lg font-semibold text-gray-900 mb-4">
//               Send Invoice - {selectedItem?.name}
//             </h3>
//             <form onSubmit={handleSendInvoice} className="space-y-4">
//               <input
//                 type="email"
//                 required
//                 value={invoiceData.customerEmail}
//                 onChange={(e) => setInvoiceData(prev => ({ ...prev, customerEmail: e.target.value }))}
//                 className="input-field"
//                 placeholder="Customer Email"
//               />
//               <textarea
//                 value={invoiceData.customerAddress}
//                 onChange={(e) => setInvoiceData(prev => ({ ...prev, customerAddress: e.target.value }))}
//                 className="input-field"
//                 rows="3"
//                 placeholder="Customer Address"
//               />
//               <textarea
//                 value={invoiceData.notes}
//                 onChange={(e) => setInvoiceData(prev => ({ ...prev, notes: e.target.value }))}
//                 className="input-field"
//                 rows="2"
//                 placeholder="Notes"
//               />
//               <div className="flex space-x-3 pt-4">
//                 <button
//                   type="button"
//                   onClick={() => setShowInvoiceModal(false)}
//                   className="flex-1 btn-secondary"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   disabled={sendingInvoice}
//                   className="flex-1 btn-primary flex items-center justify-center"
//                 >
//                   {sendingInvoice ? (
//                     <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
//                   ) : (
//                     'Send Invoice'
//                   )}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </div>
//   )
// }





















// import { useState, useEffect } from 'react'
// import { Link } from 'react-router-dom'
// import {
//   FiPlus,
//   FiSearch,
//   FiDownload,
//   FiMail,
//   FiCheck,
//   FiX,
//   FiShield,
//   FiPackage,
// } from 'react-icons/fi'
// import axios from 'axios'
// import toast from 'react-hot-toast'
// import currencies from '../../utils/currencies'

// export default function Items() {
//   const [items, setItems] = useState([])
//   const [user, setUser] = useState(null)
//   const [loading, setLoading] = useState(true)
//   const [searchTerm, setSearchTerm] = useState('')
//   const [statusFilter, setStatusFilter] = useState('all')
//   const [currencyFilter, setCurrencyFilter] = useState('all')
//   const [currentPage, setCurrentPage] = useState(1)
//   const [totalPages, setTotalPages] = useState(1)
//   const [showInvoiceModal, setShowInvoiceModal] = useState(false)
//   const [selectedItem, setSelectedItem] = useState(null)
//   const [downloadingItemId, setDownloadingItemId] = useState(null)
//   const [sendingInvoice, setSendingInvoice] = useState(false)

//   const [invoiceData, setInvoiceData] = useState({
//     customerEmail: '',
//     customerName: '',
//     customerAddress: '',
//     notes: ''
//   })

//   useEffect(() => {
//     fetchItems()
//     fetchUserProfile()
//   }, [currentPage, statusFilter, searchTerm, currencyFilter])

//   const fetchUserProfile = async () => {
//     try {
//       const response = await axios.get('/auth/profile')
//       setUser(response.data.user)
//     } catch (error) {
//       console.error('Error fetching user profile:', error)
//     }
//   }

//   const fetchItems = async () => {
//     try {
//       setLoading(true)
//       const response = await axios.get('/items', {
//         params: {
//           page: currentPage,
//           limit: 12,
//           status: statusFilter,
//           search: searchTerm,
//           currency: currencyFilter
//         }
//       })

//       setItems(response.data.items)
//       setTotalPages(response.data.totalPages)
//     } catch (error) {
//       toast.error('Error loading items')
//     } finally {
//       setLoading(false)
//     }
//   }

//   const handleDownloadInvoice = async (itemId) => {
//     setDownloadingItemId(itemId)
//     try {
//       const response = await axios.get(`/items/${itemId}/invoice`, {
//         responseType: 'blob'
//       })

//       const url = window.URL.createObjectURL(new Blob([response.data]))
//       const link = document.createElement('a')
//       link.href = url
//       link.setAttribute('download', `invoice-${itemId}.pdf`)
//       document.body.appendChild(link)
//       link.click()
//       link.remove()
//       window.URL.revokeObjectURL(url)

//       toast.success('Invoice downloaded successfully')
//     } catch (error) {
//       toast.error('Error downloading invoice')
//     } finally {
//       setDownloadingItemId(null)
//     }
//   }

//   const handleSendInvoice = async (e) => {
//     e.preventDefault()
//     setSendingInvoice(true)

//     try {
//       await axios.post(`/items/${selectedItem._id}/send-invoice`, invoiceData)
//       toast.success('Invoice sent successfully')
//       setShowInvoiceModal(false)
//       setInvoiceData({
//         customerEmail: '',
//         customerName: '',
//         customerAddress: '',
//         notes: ''
//       })
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error sending invoice')
//     } finally {
//       setSendingInvoice(false)
//     }
//   }

//   const handlePaymentApproval = async (itemId, approve) => {
//     try {
//       await axios.post(`/items/${itemId}/approve-payment`, { approve })
//       toast.success(`Payment ${approve ? 'approved' : 'rejected'} successfully`)
//       fetchItems()
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error processing payment approval')
//     }
//   }

//   const getStatusBadge = (status) => {
//     const badges = {
//       paid: 'bg-success-100 text-success-800',
//       unpaid: 'bg-error-100 text-error-800',
//       pending: 'bg-warning-100 text-warning-800'
//     }
//     return badges[status] || 'bg-gray-100 text-gray-800'
//   }

//   const statusOptions = [
//     { value: 'all', label: 'All Items' },
//     { value: 'paid', label: 'Paid' },
//     { value: 'unpaid', label: 'Unpaid' },
//     { value: 'pending', label: 'Pending' }
//   ]

//   return (
//     <div className="max-w-7xl mx-auto">
//       {/* Header */}
//       <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
//         <div>
//           <h1 className="text-2xl font-bold text-gray-900 mb-2">My Items</h1>
//           <p className="text-gray-600">Manage your marketplace items and invoices</p>
//         </div>
//         <Link to="/add-item" className="btn-primary mt-4 sm:mt-0">
//           <FiPlus className="w-4 h-4 mr-2" />
//           Add New Item
//         </Link>
//       </div>

//       {/* Admin Notice */}
//       {!user?.adminConfig?.isAdmin && (
//         <div className="card bg-blue-50 border-blue-200 mb-6">
//           <div className="flex items-start space-x-3">
//             <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
//               <FiShield className="w-4 h-4 text-blue-600" />
//             </div>
//             <div>
//               <h3 className="text-sm font-semibold text-blue-900 mb-1">Edit & Delete Permissions</h3>
//               <p className="text-sm text-blue-800">
//                 Only User Admins have permission to edit or delete items. Regular users are restricted.
//               </p>
//               <Link
//                 to="/admin-access"
//                 className="text-sm text-blue-600 hover:text-blue-700 font-medium mt-2 inline-block"
//               >
//                 Get Admin Access →
//               </Link>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Filters */}
//       <div className="card mb-6">
//         <div className="flex flex-col sm:flex-row gap-4">
//           <div className="flex-1 relative">
//             <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
//             <input
//               type="text"
//               placeholder="Search items..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               className="input-field pl-10"
//             />
//           </div>
//           <div className="sm:w-48">
//             <select
//               value={statusFilter}
//               onChange={(e) => setStatusFilter(e.target.value)}
//               className="input-field"
//             >
//               {statusOptions.map(option => (
//                 <option key={option.value} value={option.value}>
//                   {option.label}
//                 </option>
//               ))}
//             </select>
//           </div>
//           <div className="sm:w-56">
//             <select
//               value={currencyFilter}
//               onChange={(e) => setCurrencyFilter(e.target.value)}
//               className="input-field"
//             >
//               <option value="all">All Currencies</option>
//               {currencies.map(c => (
//                 <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
//               ))}
//             </select>
//           </div>
//         </div>
//       </div>

//       {/* Items */}
//       {loading ? (
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//           {[...Array(6)].map((_, i) => (
//             <div key={i} className="card animate-pulse">
//               <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
//               <div className="h-4 bg-gray-200 rounded mb-2"></div>
//               <div className="h-4 bg-gray-200 rounded w-2/3"></div>
//             </div>
//           ))}
//         </div>
//       ) : items.length > 0 ? (
//         <>
//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
//             {items.map((item) => (
//               <div key={item._id} className="card hover:shadow-lg transition-shadow">
//                 {item.image && (
//                   <img src={item.image} alt={item.name} className="w-full h-48 object-cover rounded-lg mb-4" />
//                 )}
//                 <div className="flex justify-between items-start mb-2">
//                   <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
//                   <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
//                     {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
//                   </span>
//                 </div>
//                 <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>
//                 <div className="flex justify-between items-center mb-4">
//                   <span className="text-2xl font-bold text-primary-600">{item.currency || 'NGN'} {item.price?.toLocaleString()}</span>
//                   {item.category && (
//                     <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{item.category}</span>
//                   )}
//                 </div>
//                 <div className="text-xs text-gray-500 mb-4">
//                   <p>Invoice: {item.invoiceNumber}</p>
//                   <p>Created: {new Date(item.createdAt).toLocaleDateString()}</p>
//                 </div>

//                 {item.paymentRequest && (
//                   <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
//                     <p className="text-sm text-yellow-800 mb-2">Payment confirmation requested</p>
//                     <div className="flex space-x-2">
//                       <button
//                         onClick={() => handlePaymentApproval(item._id, true)}
//                         className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center"
//                       >
//                         <FiCheck className="mr-1 w-3 h-3" /> Approve
//                       </button>
//                       <button
//                         onClick={() => handlePaymentApproval(item._id, false)}
//                         className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 flex items-center"
//                       >
//                         <FiX className="mr-1 w-3 h-3" /> Reject
//                       </button>
//                     </div>
//                   </div>
//                 )}

//                 <div className="flex justify-between items-center border-t pt-4">
//                   <div className="flex space-x-2">
//                     <button
//                       onClick={() => handleDownloadInvoice(item._id)}
//                       disabled={downloadingItemId === item._id}
//                       className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50 transition"
//                       title="Download Invoice"
//                     >
//                       {downloadingItemId === item._id ? (
//                         <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
//                       ) : (
//                         <FiDownload className="w-4 h-4" />
//                       )}
//                     </button>
//                     <button
//                       onClick={() => {
//                         setSelectedItem(item)
//                         setShowInvoiceModal(true)
//                       }}
//                       className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
//                       title="Send Invoice"
//                     >
//                       <FiMail className="w-4 h-4" />
//                     </button>
//                   </div>
//                   {!user?.adminConfig?.isAdmin && (
//                     <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">Admin Only</div>
//                   )}
//                 </div>
//               </div>
//             ))}
//           </div>

//           {/* Pagination */}
//           {totalPages > 1 && (
//             <div className="flex justify-center gap-2">
//               <button
//                 onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
//                 disabled={currentPage === 1}
//                 className="px-4 py-2 border rounded disabled:opacity-50"
//               >
//                 Previous
//               </button>
//               {[...Array(totalPages)].map((_, i) => (
//                 <button
//                   key={i}
//                   onClick={() => setCurrentPage(i + 1)}
//                   className={`px-4 py-2 border rounded ${
//                     currentPage === i + 1 ? 'bg-primary-600 text-white' : 'hover:bg-gray-50'
//                   }`}
//                 >
//                   {i + 1}
//                 </button>
//               ))}
//               <button
//                 onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
//                 disabled={currentPage === totalPages}
//                 className="px-4 py-2 border rounded disabled:opacity-50"
//               >
//                 Next
//               </button>
//             </div>
//           )}
//         </>
//       ) : (
//         <div className="text-center py-12">
//           <FiPackage className="w-16 h-16 text-gray-400 mx-auto mb-4" />
//           <h3 className="text-lg font-medium text-gray-900 mb-2">No items found</h3>
//           <p className="text-gray-600 mb-6">
//             {searchTerm || statusFilter !== 'all'
//               ? 'Try adjusting your search or filter criteria'
//               : 'Get started by adding your first item'}
//           </p>
//           <Link to="/add-item" className="btn-primary">
//             <FiPlus className="w-4 h-4 mr-2" />
//             Add Your First Item
//           </Link>
//         </div>
//       )}

//       {/* Send Invoice Modal */}
//       {showInvoiceModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-xl max-w-md w-full p-6">
//             <h3 className="text-lg font-semibold text-gray-900 mb-4">
//               Send Invoice - {selectedItem?.name}
//             </h3>
//             <form onSubmit={handleSendInvoice} className="space-y-4">
//               <input
//                 type="email"
//                 required
//                 value={invoiceData.customerEmail}
//                 onChange={(e) => setInvoiceData(prev => ({ ...prev, customerEmail: e.target.value }))}
//                 className="input-field"
//                 placeholder="Customer Email"
//               />
//               <input
//                 type="text"
//                 required
//                 value={invoiceData.customerName}
//                 onChange={(e) => setInvoiceData(prev => ({ ...prev, customerName: e.target.value }))}
//                 className="input-field"
//                 placeholder="Customer Name"
//               />
//               <textarea
//                 value={invoiceData.customerAddress}
//                 onChange={(e) => setInvoiceData(prev => ({ ...prev, customerAddress: e.target.value }))}
//                 className="input-field"
//                 rows="3"
//                 placeholder="Customer Address"
//               />
//               <textarea
//                 value={invoiceData.notes}
//                 onChange={(e) => setInvoiceData(prev => ({ ...prev, notes: e.target.value }))}
//                 className="input-field"
//                 rows="2"
//                 placeholder="Notes"
//               />
//               <div className="flex space-x-3 pt-4">
//                 <button
//                   type="button"
//                   onClick={() => setShowInvoiceModal(false)}
//                   className="flex-1 btn-secondary"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   disabled={sendingInvoice}
//                   className="flex-1 btn-primary flex items-center justify-center"
//                 >
//                   {sendingInvoice ? (
//                     <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
//                   ) : (
//                     'Send Invoice'
//                   )}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </div>
//   )
// }
